<template>
    <div class="">
        <h3 class="w-full font-sans font-medium text-center text-blue-500 h2">
            Select a service
        </h3>
        <div class="flex justify-center mb-6">
            <button class="flex items-center mt-6 font-normal text-center text-blue-500">
                Eye Test

                <!-- prettier-ignore -->
                <svg class="ml-2" width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg" role="img" focusable="false" aria-label="">
                <path d="M14.7499 4.16774C14.7505 4.07562 14.7328 3.9843 14.698 3.89902C14.6632 3.81374 14.6118 3.73618 14.547 3.67077L11.5792 0.702997C11.5138 0.638124 11.4362 0.586801 11.3509 0.551968C11.2657 0.517135 11.1743 0.499479 11.0822 0.500012C10.9901 0.499479 10.8988 0.517135 10.8135 0.551968C10.7282 0.586801 10.6507 0.638124 10.5853 0.702997L8.6044 2.68385L0.953973 10.3343C0.889101 10.3997 0.837777 10.4772 0.802945 10.5625C0.768112 10.6478 0.750456 10.7391 0.750988 10.8312V13.799C0.750988 13.9847 0.824733 14.1627 0.955998 14.294C1.08726 14.4252 1.2653 14.499 1.45094 14.499H4.41871C4.51666 14.5043 4.61462 14.489 4.70627 14.454C4.79791 14.419 4.88118 14.3652 4.95067 14.296L12.5591 6.64555L14.547 4.6997C14.6108 4.63186 14.6629 4.55378 14.7009 4.46872C14.7077 4.41292 14.7077 4.35652 14.7009 4.30073C14.7042 4.26815 14.7042 4.23532 14.7009 4.20273L14.7499 4.16774ZM4.13174 13.0991H2.15088V11.1182L9.10137 4.16774L11.0822 6.14859L4.13174 13.0991ZM12.0691 5.16166L10.0883 3.18081L11.0822 2.19389L13.0561 4.16774L12.0691 5.16166Z" fill="#004876"/>
                </svg>
            </button>
        </div>

        <div role="radiogroup" aria-labelledby="isMember" class="">
            <BaseLabel
                id="isMember"
                class="pt-4"
                :text="'Are you a Health Partners member?'"
                :validation="$v.isMember"
                :error-outline="false"
            >
                <div class="flex">
                    <BaseRadio
                        v-for="(option, index) in ['Yes', 'No']"
                        :key="index"
                        class="flex-grow mx-2 first:ml-0 last:mr-0"
                        :active="option == isMember"
                        :value="option"
                        :disabled="isLoading"
                        :text="option"
                        @input="conditionalUpdate('isMember', $event, 'Yes')"
                    />
                </div>
            </BaseLabel>
        </div>

        <div role="radiogroup" aria-labelledby="emergencyAppointment" class="">
            <BaseLabel
                id="emergencyAppointment"
                class="pt-4"
                :text="'Is this an emergency or are you experiencing loss of sight?'"
                :validation="$v.emergencyAppointment"
                :error-outline="false"
            >
                <div class="flex">
                    <BaseRadio
                        v-for="(option, index) in ['Yes', 'No']"
                        :key="index"
                        class="flex-grow mx-2 first:ml-0 last:mr-0"
                        :active="option == emergencyAppointment"
                        :value="option"
                        :disabled="isLoading"
                        :text="option"
                        @input="conditionalUpdate('emergencyAppointment', $event, 'No')"
                    />
                </div>
            </BaseLabel>
        </div>

        <div role="radiogroup" aria-labelledby="multipleAppointments" class="">
            <BaseLabel
                id="multipleAppointments"
                class="pt-4"
                :text="'Do you need to book two or more appointments together at the same time?'"
                :validation="$v.multipleAppointments"
                :error-outline="false"
            >
                <div class="flex">
                    <BaseRadio
                        v-for="(option, index) in ['Yes', 'No']"
                        :key="index"
                        class="flex-grow mx-2 first:ml-0 last:mr-0"
                        :active="option == multipleAppointments"
                        :value="option"
                        :disabled="isLoading"
                        :text="option"
                        @input="conditionalUpdate('multipleAppointments', $event, 'No')"
                    />
                </div>
            </BaseLabel>
        </div>

        <div role="radiogroup" aria-labelledby="ongoingCondition" class="">
            <BaseLabel
                id="ongoingCondition"
                class="pt-4"
                text="Are you diabetic or do you need an appointment to monitor your eye health in relation to an ongoing condition?"
                :validation="$v.ongoingCondition"
                :error-outline="false"
            >
                <div class="flex">
                    <BaseRadio
                        v-for="(option, index) in ['Yes', 'No']"
                        :key="index"
                        class="flex-grow mx-2 first:ml-0 last:mr-0"
                        :active="option == ongoingCondition"
                        :disabled="isLoading"
                        :value="option"
                        :text="option"
                        @input="conditionalUpdate('ongoingCondition', $event, 'No')"
                    />
                </div>
            </BaseLabel>
        </div>

        <div role="radiogroup" aria-labelledby="specialistAppointment" class="">
            <BaseLabel
                id="specialistAppointment"
                class="pt-4"
                :text="'Do you need a contact lens appointment?'"
                :validation="$v.specialistAppointment"
                :error-outline="false"
            >
                <div class="flex">
                    <BaseRadio
                        v-for="(option, index) in ['Yes', 'No']"
                        :key="index"
                        class="flex-grow mx-2 first:ml-0 last:mr-0"
                        :active="option == specialistAppointment"
                        :value="option"
                        :disabled="isLoading"
                        :text="option"
                        @input="conditionalUpdate('specialistAppointment', $event, 'No')"
                    />
                </div>
            </BaseLabel>
        </div>

        <!-- <button
            class="w-full mt-10 btn"
            :disabled="$v.$invalid"
            :class="{
                'btn--ghost-grey opacity-75': $v.$invalid,
                'btn--loading text-blue-500': isLoading
            }"
            @click="submit"
        >
            Continue
        </button> -->
    </div>
</template>

<script>
import { required, sameAs } from 'vuelidate/lib/validators';
import hydrateFromProps from '@/../vue/online-booking/mixins/hydrate-from-props';
import BaseLabel from '@/../vue/_base/form/Label.vue';
import BaseRadio from '@/../vue/_base/form/Radio.vue';
import ServiceTypeUtils from '@/../vue/online-booking/mixins/service-type-utils';

export default {
    components: {
        BaseRadio,
        BaseLabel
    },

    mixins: [ServiceTypeUtils, hydrateFromProps(['serviceQuestions'])],

    props: {
        serviceType: {
            type: String,
            required: true
        },
        serviceQuestions: {
            type: Object,
            required: true
        },
        isLoading: {
            type: Boolean,
            required: true
        }
    },

    data: () => ({
        isMember: undefined,
        emergencyAppointment: undefined,
        multipleAppointments: undefined,
        specialistAppointment: undefined,
        ongoingCondition: undefined
    }),

    validations: {
        isMember: {
            required,
            sameAs: sameAs(() => 'Yes')
        },
        emergencyAppointment: {
            required,
            sameAs: sameAs(() => 'No')
        },
        multipleAppointments: {
            required,
            sameAs: sameAs(() => 'No')
        },
        specialistAppointment: {
            required,
            sameAs: sameAs(() => 'No')
        },
        ongoingCondition: {
            required,
            sameAs: sameAs(() => 'No')
        }
    },

    watch: {
        $v: {
            handler() {
                if (!this.$v.$invalid) {
                    this.submit();
                }
            },
            deep: true
        }
    },

    methods: {
        submit() {
            this.$emit('submit', { serviceQuestions: this._data });
        },

        conditionalUpdate(fieldHandle, value, validValue) {
            if (value == validValue) {
                this[fieldHandle] = value;
            } else {
                this.$modal.show(`${fieldHandle}${this.serviceType}`);
            }
        },

        back() {
            this.$emit('back');
        }
    }
};
</script>
